/**
 * CompoundComparePane — interactive 2-column comparison surface.
 *
 * Promotes the previously chat-only `compoundComparisonShortcut` into a
 * SidePanel page. Two compound-picker dropdowns at top; column-
 * aligned property + energy diff below; "Save comparison" button
 * writes a DecisionRecord via gibbs-decision-record-export.
 *
 * v1 chemistry coverage:
 *   - All numeric properties + energies available from
 *     getCompoundData(). Delta highlighting where one column is
 *     better than the other (lower-is-better for energies/scores;
 *     property comparisons are neutral).
 *   - Flag count (toxicophore alert badges) per compound, honoring
 *     the scanner-unavailable null sentinel.
 *   - 2D structure render is deferred to v2 (needs SmilesDrawerReact
 *     instantiation under the SidePanel width budget).
 *
 * Theme A's `MitigationVisual` is composable but not wired here —
 * users invoke that path via the medchem advice shortcut.
 *
 * @module ui/dialog/CompoundComparePane/CompoundComparePane
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
    Box, Typography, Select, MenuItem, FormControl, InputLabel,
    Button, Chip, Tooltip, TextField, Alert,
} from '@mui/material';
import { App } from 'BMapsSrc/BMapsApp';
import { SidePanelContainer } from '../../info_display/SidePanel';
import { ErrorBoundary } from '../../PageWrapper';
import useEventBrokerSubscription from '../../common/helpers/useEventBrokerSubscription';
import { getCompoundData } from '../../../gibbs/text-to-action/serializeWorkspace';
import {
    CompoundComparePaneTitle, CompoundComparePaneEvents,
} from './constants';
import {
    createDecisionRecord, saveDecisionRecord,
} from '../../../gibbs/decision-records';
import { MitigationVisual } from '../../MitigationVisual';

const NUMERIC_PROPS = [
    { key: 'MW', label: 'MW', decimals: 0, lowerIsBetter: false },
    { key: 'logP', label: 'logP', decimals: 1, lowerIsBetter: false },
    { key: 'PSA', label: 'PSA', decimals: 0, lowerIsBetter: false },
    { key: 'HBA', label: 'HBA', decimals: 0, lowerIsBetter: false },
    { key: 'HBD', label: 'HBD', decimals: 0, lowerIsBetter: false },
    { key: 'rotatableBonds', label: 'Rot. bonds', decimals: 0, lowerIsBetter: false },
    { key: 'heavyAtoms', label: 'Heavy atoms', decimals: 0, lowerIsBetter: false },
];

const NUMERIC_ENERGIES = [
    { key: 'gninaScore', label: 'GNINA score', lowerIsBetter: true },
    { key: 'gninaAffinity', label: 'GNINA affinity', lowerIsBetter: true },
    { key: 'dockingScore', label: 'Docking score', lowerIsBetter: true },
    { key: 'interactionScore', label: 'Interaction', lowerIsBetter: true },
    { key: 'vdW', label: 'vdW', lowerIsBetter: true },
    { key: 'electrostatics', label: 'Electrostatics', lowerIsBetter: true },
    { key: 'hbonds', label: 'H-bonds', lowerIsBetter: true },
    { key: 'solvation', label: 'ddGs', lowerIsBetter: true },
    { key: 'stress', label: 'Stress', lowerIsBetter: true },
    { key: 'total', label: 'Total', lowerIsBetter: true },
];

function fmtNumber(val, decimals = 1) {
    if (val == null || !Number.isFinite(val)) return '—';
    return val.toFixed(decimals);
}

function flagsChip(flags) {
    if (flags === null || flags === undefined) {
        return { label: '?', tooltip: 'Scanner unavailable' };
    }
    if (!Array.isArray(flags) || flags.length === 0) {
        return { label: 'clean', tooltip: 'No toxicophore flags' };
    }
    return {
        label: `${flags.length} flag${flags.length === 1 ? '' : 's'}`,
        tooltip: flags.join(', '),
    };
}

/**
 * Compute diff direction for a row. Returns 'a-wins', 'b-wins', 'tie',
 * or 'unknown'.
 */
function diffDirection(a, b, lowerIsBetter) {
    if (a == null || b == null || !Number.isFinite(a) || !Number.isFinite(b)) {
        return 'unknown';
    }
    if (a === b) return 'tie';
    const aWins = lowerIsBetter ? a < b : a > b;
    return aWins ? 'a-wins' : 'b-wins';
}

function DiffCell({ value, decimals, winning }) {
    let bg = 'transparent';
    if (winning) bg = 'rgba(76, 175, 80, 0.18)';
    return (
        <Box sx={{
            px: 0.75,
            py: 0.25,
            textAlign: 'right',
            backgroundColor: bg,
            borderRadius: 0.5,
            fontFamily: 'monospace',
            fontSize: '0.85em',
        }}>
            {fmtNumber(value, decimals)}
        </Box>
    );
}

export default function CompoundComparePane({
    open, setOpen, handleClose,
}) {
    const [compounds, setCompounds] = useState([]);
    const [idA, setIdA] = useState(null);
    const [idB, setIdB] = useState(null);
    const [note, setNote] = useState('');
    const [saveStatus, setSaveStatus] = useState(null);

    // Refresh compound list whenever the pane opens or the workspace
    // emits a compounds-changed-equivalent event. Cheap to recompute.
    //
    // v2 (2026-05-15): also pull SMILES from each raw Compound so the
    // 2D-pair render below can hand SMILES to MitigationVisual. The
    // anonymized getCompoundData() path doesn't include SMILES, so we
    // zip raw + anonymized by index.
    const refreshCompounds = useCallback(() => {
        const workspace = App.Workspace;
        if (!workspace) {
            setCompounds([]);
            return;
        }
        const data = getCompoundData(workspace);
        const raw = workspace.getLoadedCompounds?.() || [];
        // Index alignment: getCompoundData uses 1-based ids matching
        // the raw compound's 0-based array index + 1.
        for (const entry of data) {
            const rawIdx = (entry.id || 1) - 1;
            const rawCompound = raw[rawIdx];
            const smi = rawCompound && typeof rawCompound.getSmiles === 'function'
                ? rawCompound.getSmiles()
                : rawCompound?.smiles || null;
            entry.smiles = smi;
        }
        setCompounds(data);
    }, []);

    useEffect(() => {
        if (open) refreshCompounds();
    }, [open, refreshCompounds]);

    // Open via EventBroker (handed off from the chat shortcut)
    const handleOpenWith = useCallback((_evt, data) => {
        const ids = data && Array.isArray(data.compoundIds)
            ? data.compoundIds : [];
        refreshCompounds();
        if (ids.length >= 1) setIdA(ids[0]);
        if (ids.length >= 2) setIdB(ids[1]);
        if (setOpen) setOpen(true);
    }, [setOpen, refreshCompounds]);
    useEventBrokerSubscription(CompoundComparePaneEvents.OPEN_WITH, handleOpenWith);

    const compA = useMemo(
        () => compounds.find((c) => c.id === idA) || null,
        [compounds, idA]
    );
    const compB = useMemo(
        () => compounds.find((c) => c.id === idB) || null,
        [compounds, idB]
    );

    const canSave = compA && compB && idA !== idB;

    const handleSave = useCallback(async () => {
        if (!canSave) return;
        setSaveStatus({ kind: 'pending' });
        try {
            const workspace = App.Workspace;
            const protein = workspace?.getLoadedProtein?.();
            const pdbId = protein?.pdbId || protein?.id || null;
            const draft = createDecisionRecord({
                reviewer: { id: 'self' },
                workspace: pdbId ? { proteinPdbId: pdbId } : {},
                decisionContext: {
                    triggerSurface: 'compound-compare',
                    rationaleSnapshot: null,
                    rankedCompounds: [compA, compB].map((c) => ({
                        id: c.id,
                        primaryScore: c.energies?.gninaScore
                            ?? c.energies?.dockingScore
                            ?? null,
                        properties: c.properties || {},
                        flags: c.flags === undefined ? null : c.flags,
                    })),
                    // v1: no "user picked" — caller may extend.
                    userPicked: 'defer',
                },
                reviewerNote: note,
            });
            const { record, error } = await saveDecisionRecord(draft);
            if (error) {
                setSaveStatus({ kind: 'error', message: error });
            } else {
                setSaveStatus({ kind: 'success', id: record.id });
            }
        } catch (err) {
            setSaveStatus({ kind: 'error', message: err?.message || String(err) });
        }
    }, [canSave, compA, compB, note]);

    return (
        <SidePanelContainer
            title={CompoundComparePaneTitle}
            open={open}
            handleClose={handleClose}
            longLived
        >
            <ErrorBoundary surfaceName="Compare">
                <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, gap: 1, minHeight: 0 }}>
                    {/* Picker row */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel id="compare-a-label">Compound A</InputLabel>
                            <Select
                                labelId="compare-a-label"
                                label="Compound A"
                                value={idA ?? ''}
                                onChange={(e) => setIdA(e.target.value || null)}
                            >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {compounds.map((c) => (
                                    <MenuItem key={`a-${c.id}`} value={c.id}>
                                        {`#${c.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel id="compare-b-label">Compound B</InputLabel>
                            <Select
                                labelId="compare-b-label"
                                label="Compound B"
                                value={idB ?? ''}
                                onChange={(e) => setIdB(e.target.value || null)}
                            >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {compounds.map((c) => (
                                    <MenuItem key={`b-${c.id}`} value={c.id}>
                                        {`#${c.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {compounds.length === 0 && (
                        <Alert severity="info" variant="outlined">
                            Load compounds into the workspace, then return
                            here to compare any two.
                        </Alert>
                    )}

                    {compA && compB && idA === idB && (
                        <Alert severity="warning" variant="outlined">
                            Select two different compounds.
                        </Alert>
                    )}

                    {/* v2 (2026-05-15): 2D structure pair render.
                        Reuses MitigationVisual with no atom highlights
                        for plain 2D rendering. Skipped when either
                        compound is missing SMILES (e.g., legacy
                        workspace path). */}
                    {compA && compB && idA !== idB
                        && (compA.smiles || compB.smiles) && (
                        <StructurePair a={compA} b={compB} />
                    )}

                    {/* Diff table */}
                    {compA && compB && idA !== idB && (
                        <DiffTable a={compA} b={compB} />
                    )}

                    {/* Save section */}
                    <Box sx={{
                        borderTop: '1px solid', borderColor: 'divider',
                        pt: 1, mt: 'auto',
                    }}>
                        <TextField
                            label="Reviewer note (optional)"
                            placeholder="e.g., compound 1 wins for kinase pocket fit"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            sx={{ mb: 1 }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            disabled={!canSave}
                            onClick={handleSave}
                            fullWidth
                        >
                            Save comparison as decision record
                        </Button>
                        {saveStatus?.kind === 'success' && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                                {`Saved (id ${saveStatus.id.slice(0, 8)}…).`}
                            </Alert>
                        )}
                        {saveStatus?.kind === 'error' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {saveStatus.message}
                            </Alert>
                        )}
                    </Box>
                </Box>
            </ErrorBoundary>
        </SidePanelContainer>
    );
}

function DiffTable({ a, b }) {
    return (
        <Box sx={{
            overflowY: 'auto', flex: 1, minHeight: 0,
            fontSize: '0.85em',
        }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 0.25,
                alignItems: 'center',
            }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    Property
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                    {`#${a.id}`}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                    {`#${b.id}`}
                </Typography>

                {/* Flags row (special — uses chips, not numeric diff) */}
                <Typography variant="caption">Flags</Typography>
                <FlagsCellChip flags={a.flags} />
                <FlagsCellChip flags={b.flags} />

                {/* Numeric energy rows */}
                {NUMERIC_ENERGIES.map(({ key, label, lowerIsBetter }) => {
                    const va = a.energies?.[key];
                    const vb = b.energies?.[key];
                    if (va == null && vb == null) return null;
                    const dir = diffDirection(va, vb, lowerIsBetter);
                    return [
                        <Typography key={`l-${key}`} variant="caption">{label}</Typography>,
                        <DiffCell key={`a-${key}`} value={va} decimals={1} winning={dir === 'a-wins'} />,
                        <DiffCell key={`b-${key}`} value={vb} decimals={1} winning={dir === 'b-wins'} />,
                    ];
                })}

                {/* Numeric property rows */}
                {NUMERIC_PROPS.map(({ key, label, decimals }) => {
                    const va = a.properties?.[key];
                    const vb = b.properties?.[key];
                    if (va == null && vb == null) return null;
                    return [
                        <Typography key={`l-p-${key}`} variant="caption">{label}</Typography>,
                        <DiffCell key={`a-p-${key}`} value={va} decimals={decimals} winning={false} />,
                        <DiffCell key={`b-p-${key}`} value={vb} decimals={decimals} winning={false} />,
                    ];
                })}
            </Box>
        </Box>
    );
}

function FlagsCellChip({ flags }) {
    const { label, tooltip } = flagsChip(flags);
    let color = 'success';
    if (label === '?') color = 'default';
    else if (label !== 'clean') color = 'warning';
    return (
        <Tooltip title={tooltip}>
            <Chip
                label={label}
                size="small"
                color={color}
                sx={{ height: 18, fontSize: '0.72em', justifySelf: 'end' }}
            />
        </Tooltip>
    );
}

/**
 * Side-by-side 2D structure render. Reuses MitigationVisual with
 * empty atomIndices — the component already handles the no-highlight
 * case as a plain SVG render. Width is constrained so the pair fits
 * inside the SidePanel column.
 */
function StructurePair({ a, b }) {
    const PAIR_WIDTH = 130;
    const PAIR_HEIGHT = 110;
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0.5,
            mt: 0.5,
        }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: 'center', display: 'block' }}
                >
                    {`#${a.id}`}
                </Typography>
                {a.smiles ? (
                    <MitigationVisual
                        smiles={a.smiles}
                        atomIndices={[]}
                        width={PAIR_WIDTH}
                        height={PAIR_HEIGHT}
                    />
                ) : (
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ textAlign: 'center', display: 'block', py: 1 }}
                    >
                        (no SMILES)
                    </Typography>
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: 'center', display: 'block' }}
                >
                    {`#${b.id}`}
                </Typography>
                {b.smiles ? (
                    <MitigationVisual
                        smiles={b.smiles}
                        atomIndices={[]}
                        width={PAIR_WIDTH}
                        height={PAIR_HEIGHT}
                    />
                ) : (
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ textAlign: 'center', display: 'block', py: 1 }}
                    >
                        (no SMILES)
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
